<template>
  <div>
    <v-row v-if="platform">
      <v-col cols="12">
        <div class="d-flex align-center justify-end mb-3">
          <v-btn small color="primary" @click="openPreview">
            Abrir Preview
            <v-icon right small>mdi-open-in-new</v-icon>
          </v-btn>
        </div>
        <v-expansion-panels focusable v-model="expansePanel">
          <v-expansion-panel v-for="(ep, i) in expansionsPanels" :key="ep.name">
            <v-expansion-panel-header class="justify-start" disable-icon-rotate>
              <div class="d-flex align-center" style="gap: 1rem">
                <v-icon>{{ ep.icon }}</v-icon>
                <h6 class="mb-0">{{ ep.title }}</h6>
              </div>
              <template v-slot:actions>
                <v-btn
                  small
                  icon
                  v-if="!!ep.saving"
                  :loading="ep.saving.loading"
                >
                  <v-icon :color="ep.saving.error ? 'error' : 'success'">
                    {{
                      ep.saving.error
                        ? "mdi-alert-circle"
                        : "mdi-content-save-check"
                    }}
                  </v-icon>
                </v-btn>
                <v-btn
                  icon
                  v-else-if="expansePanel !== i || expansePanel == null"
                >
                  <v-icon>mdi-chevron-down</v-icon>
                </v-btn>
                <v-btn icon color="primary" v-else>
                  <v-icon>mdi-content-save</v-icon>
                </v-btn>
              </template>
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <component
                :is="ep.component"
                v-model="platform"
                :old-value="oldPlatform"
                :ref="ep.name"
              ></component>
              <v-alert v-if="ep.error" type="error" class="mb-0 mt-1">
                {{ ep.error }}
              </v-alert>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </v-col>
    </v-row>

    <PlatformDeploymentStatus v-if="platform.id" :platform="platform" />
  </div>
</template>

<script>
import platformService from "@/services/admin/platform";
import BasicInfoStep from "./steps/BasicInfoStep.vue";
import LogosStep from "./steps/LogosStep.vue";
import ColorsStep from "./steps/ColorsStep.vue";
import LegalInfoStep from "./steps/LegalInfoStep.vue";
import SocialNetworksStep from "./steps/SocialNetworksStep.vue";
import PartyShareStep from "./steps/PartyShareStep.vue";
import SettingsStep from "./steps/SettingsStep.vue";
import PlatformDeploymentStatus from "./PlatformDeploymentStatus.vue";

const defaultPlatform = () => ({
  name: "",
  domain: "",
  email: "",
  phone: "",
  logos: {
    dark: null,
    light: null,
    folks: null,
    favicon: null,
    background: null,
    videoCover: null,
  },
  colors: {
    dark: {
      primary: "",
      secondary: "",
      brandPrimary: "",
      brandSecondary: "",
    },
    light: {
      primary: "",
      secondary: "",
      brandPrimary: "",
      brandSecondary: "",
    },
    appBar: "",
    footerBar: "",
  },
  legalInfo: {
    document: "",
    legalName: "",
    fantasyName: "",
  },
  settings: {
    artistsHome: true,
    galleryHome: false,
    proconPhone: false,
    hideRefundPolicy: false,
    headerPartyHighlight: false,
    homeHighlight: false,
    supportContact: "",
    sacContact: "",
    financeContact: "",
  },
  socialNetworks: [],
});

export default {
  name: "PlatformDesign",
  components: {
    BasicInfoStep,
    LogosStep,
    ColorsStep,
    LegalInfoStep,
    SocialNetworksStep,
    PartyShareStep,
    SettingsStep,
    PlatformDeploymentStatus,
  },
  props: {
    platform: {
      type: Object,
      required: true,
      default: defaultPlatform,
      validator: function (value) {
        return value !== null;
      },
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      broadcastChannel: null,
      expansePanel: 0,
      expansionsPanels: [
        {
          name: "basic",
          icon: "mdi-information",
          title: "Informações Básicas",
          component: "BasicInfoStep",
          saving: false,
        },
        {
          name: "logos",
          icon: "mdi-image",
          title: "Logos",
          component: "LogosStep",
          saving: false,
        },
        {
          name: "colors",
          icon: "mdi-palette",
          title: "Cores",
          component: "ColorsStep",
          saving: false,
        },
        {
          name: "legal",
          icon: "mdi-file-document",
          title: "Informações Legais",
          component: "LegalInfoStep",
          saving: false,
        },
        {
          name: "partyShare",
          icon: "mdi-calendar-check",
          title: "Compartilhamento de Eventos",
          component: "PartyShareStep",
          saving: false,
        },
        {
          name: "settings",
          icon: "mdi-cog",
          title: "Configurações",
          component: "SettingsStep",
          saving: false,
        },
        {
          name: "social",
          icon: "mdi-share-variant",
          title: "Redes Sociais",
          component: "SocialNetworksStep",
          saving: false,
        },
      ],
      // Armazenamos uma cópia do estado inicial para verificar alterações
      oldPlatform: {},
    };
  },
  created() {
    // Fazemos uma cópia profunda do objeto platform assim que o componente é criado
    this.oldPlatform = Object.assign(
      {},
      defaultPlatform(),
      structuredClone(this.platform)
    );
  },
  methods: {
    openPreview() {
      const url = "/extras/platform/preview"; // Rota do componente de preview
      window.open(url, "Preview", "width=960,height=650");
    },
    async saveGroup(index) {
      let updateData = false;
      this.expansionsPanels[index].error = false;
      const panelName = this.expansionsPanels[index].name;
      const components = this.$refs[panelName];
      const component = components[0] || null;

      if (!component) return;
      if (component?.getFormData) updateData = component.getFormData();

      if (updateData === false) {
        this.expansionsPanels[index].saving = false;
        return;
      }

      if (this.expansionsPanels[index].saving?.timeout)
        clearTimeout(this.expansionsPanels[index].saving.timeout);

      this.expansionsPanels[index].saving = {
        loading: true,
        timeout: false,
        error: false,
      };

      try {
        await platformService.update(this.platform.id, updateData);

        this.oldPlatform = JSON.parse(JSON.stringify(this.platform));

        this.$emit("refresh");

        this.expansionsPanels[index].saving = {
          loading: false,
          error: false,
        };
      } catch (e) {
        console.error(e);
        this.expansionsPanels[index].saving = {
          loading: false,
          error: true,
        };

        this.expansionsPanels[index].error =
          e.message || "Erro ao atualizar plataforma";
      } finally {
        // Após 5 segundos, remove o ícone de salvamento
        this.expansionsPanels[index].saving.timeout = setTimeout(() => {
          this.expansionsPanels[index].saving = false;
        }, 5000);
      }
    },
  },
  watch: {
    // Quando mudamos de um painel para outro, chamamos o saveGroup do painel que está saindo
    expansePanel(val, oldVal) {
      if (oldVal != undefined) {
        this.saveGroup(oldVal);
      }
      if (val != undefined) {
        this.expansionsPanels[val].saving = false;
      }
    },
    platform: {
      handler(val) {
        if (this.broadcastChannel) {
          this.broadcastChannel.postMessage(val);
        }
      },
      deep: true,
    },
  },
  mounted() {
    this.broadcastChannel = new BroadcastChannel("platform-editor");
    this.broadcastChannel.onmessage = (event) => {
      if (event.data.type === "getPlatform")
        this.broadcastChannel.postMessage(this.platform);
    };
    this.broadcastChannel.postMessage(this.platform);
  },
};
</script>
